import EventBusyIcon from "@mui/icons-material/EventBusy";
import { Box, CircularProgress, Typography } from "@mui/material";
import { CANCEL_MODAL } from "@/constants/campaignLifecycle";
import { useCancelPreview } from "@/hooks/useCampaignLifecycle";
import { formatBudget, formatDate } from "@/lib/campaigns";
import CampaignActionDialog from "./CampaignActionDialog";

interface CancelCampaignModalProps {
  open: boolean;
  campaignId: string;
  campaignName: string;
  onClose: () => void;
  onConfirm: () => void;
}

function toNumber(value: string | undefined): number {
  return value ? Number(value) : 0;
}

/** One "label … $value" line in the money breakdown. */
function MoneyRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
      <Typography sx={{ fontSize: "13.5px", color: "var(--color-graphite)" }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: "13.5px",
          fontWeight: 700,
          color: "var(--color-rodeo-black)",
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Confirm canceling a scheduled / paused-unsent campaign, with the prototype's "what happens to the
 * money" section — a read-only dry-run (`useCancelPreview`) of the exact credit return. Terminal and
 * danger-styled; dismiss reads "Keep campaign" (never "Cancel" on a dialog that cancels).
 */
export default function CancelCampaignModal({
  open,
  campaignId,
  campaignName,
  onClose,
  onConfirm,
}: CancelCampaignModalProps) {
  const { data: preview, isLoading } = useCancelPreview(campaignId, open);
  const held = toNumber(preview?.heldCredits);
  const invoiced = toNumber(preview?.invoicedDepositVoided);
  const ceiling = toNumber(preview?.ceilingReleased);
  const expired = toNumber(preview?.expiredCredits);
  const hasAny = held > 0 || invoiced > 0 || ceiling > 0;

  const money = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: "4px" }}>
      <Typography
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--color-graphite)",
        }}
      >
        {CANCEL_MODAL.moneyLabel}
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={18} sx={{ color: "var(--color-dark-dust)" }} />
        </Box>
      ) : hasAny ? (
        <>
          {held > 0 && (
            <MoneyRow
              label={CANCEL_MODAL.rowReturned(preview?.walletName ?? "your wallet")}
              value={formatBudget(held)}
            />
          )}
          {invoiced > 0 && (
            <MoneyRow label={CANCEL_MODAL.rowInvoicedVoided} value={formatBudget(invoiced)} />
          )}
          {ceiling > 0 && (
            <MoneyRow label={CANCEL_MODAL.rowCeilingReleased} value={formatBudget(ceiling)} />
          )}
          {held > 0 && preview?.earliestExpiry && (
            <Typography sx={{ fontSize: "12.5px", color: "var(--color-graphite)" }}>
              {CANCEL_MODAL.noteReturned(formatDate(preview.earliestExpiry))}
            </Typography>
          )}
          {expired > 0 && (
            <Typography sx={{ fontSize: "12.5px", color: "var(--color-good-wine)" }}>
              {CANCEL_MODAL.warnExpired(formatBudget(expired))}
            </Typography>
          )}
        </>
      ) : (
        <Typography sx={{ fontSize: "12.5px", color: "var(--color-graphite)" }}>
          {CANCEL_MODAL.noteNothingHeld}
        </Typography>
      )}
    </Box>
  );

  return (
    <CampaignActionDialog
      open={open}
      title={CANCEL_MODAL.title(campaignName)}
      icon={EventBusyIcon}
      body={CANCEL_MODAL.body}
      extra={money}
      primaryLabel={CANCEL_MODAL.confirm}
      secondaryLabel={CANCEL_MODAL.dismiss}
      danger
      onPrimary={onConfirm}
      onClose={onClose}
    />
  );
}
